/**
 * CoachPlaybook - Soccer Sport Module
 * Version: 0.6.0
 * Description: Complete soccer/football configuration — positions, actions, formations, age groups
 */

const soccer = {
  id: 'soccer',
  name: 'Soccer',
  icon: '⚽',
  accentColor: '#22c55e',
  surfaceType: 'pitch',

  views: ['full', 'half', 'attacking-third', 'defending-third'],
  defaultView: 'full',
  orientation: ['vertical', 'horizontal'],

  playersPerSide: 11,
  maxDefenders: 11,

  ageGroups: [
    { id: 'u6', label: 'U6 (3v3)', players: 3, fieldSize: '25x20yd', shotClock: null, periods: 4, periodLength: 6 },
    { id: 'u8', label: 'U8 (4v4)', players: 4, fieldSize: '40x30yd', shotClock: null, periods: 4, periodLength: 8 },
    { id: 'u10', label: 'U10 (7v7)', players: 7, fieldSize: '55x35yd', shotClock: null, periods: 2, periodLength: 25 },
    { id: 'u12', label: 'U12 (9v9)', players: 9, fieldSize: '70x50yd', shotClock: null, periods: 2, periodLength: 30 },
    { id: 'u14', label: 'U14 (11v11)', players: 11, fieldSize: '100x60yd', shotClock: null, periods: 2, periodLength: 35 },
    { id: 'high', label: 'High School', players: 11, fieldSize: '110x70yd', shotClock: null, periods: 2, periodLength: 40 },
    { id: 'college', label: 'College', players: 11, fieldSize: '115x75yd', shotClock: null, periods: 2, periodLength: 45 },
    { id: 'pro', label: 'Professional', players: 11, fieldSize: '115x75yd', shotClock: null, periods: 2, periodLength: 45 }
  ],

  positions: [
    // Goalkeeper
    { id: 'GK', name: 'Goalkeeper', short: 'GK', number: 1, defaultColor: '#fbbf24', role: 'goalkeeper' },
    // Defenders
    { id: 'LB', name: 'Left Back', short: 'LB', number: 3, defaultColor: '#3b82f6', role: 'defense' },
    { id: 'CB', name: 'Center Back', short: 'CB', number: 4, defaultColor: '#2563eb', role: 'defense' },
    { id: 'RB', name: 'Right Back', short: 'RB', number: 2, defaultColor: '#3b82f6', role: 'defense' },
    { id: 'LWB', name: 'Left Wing Back', short: 'LWB', number: 6, defaultColor: '#60a5fa', role: 'defense' },
    { id: 'RWB', name: 'Right Wing Back', short: 'RWB', number: 7, defaultColor: '#60a5fa', role: 'defense' },
    { id: 'SW', name: 'Sweeper', short: 'SW', number: 5, defaultColor: '#1e40af', role: 'defense' },
    // Midfield
    { id: 'CDM', name: 'Defensive Midfielder', short: 'CDM', number: 6, defaultColor: '#059669', role: 'midfield' },
    { id: 'CM', name: 'Central Midfielder', short: 'CM', number: 8, defaultColor: '#10b981', role: 'midfield' },
    { id: 'CAM', name: 'Attacking Midfielder', short: 'CAM', number: 10, defaultColor: '#34d399', role: 'midfield' },
    { id: 'LM', name: 'Left Midfielder', short: 'LM', number: 11, defaultColor: '#10b981', role: 'midfield' },
    { id: 'RM', name: 'Right Midfielder', short: 'RM', number: 7, defaultColor: '#10b981', role: 'midfield' },
    // Forwards
    { id: 'ST', name: 'Striker', short: 'ST', number: 9, defaultColor: '#dc2626', role: 'forward' },
    { id: 'CF', name: 'Center Forward', short: 'CF', number: 9, defaultColor: '#ef4444', role: 'forward' },
    { id: 'LW', name: 'Left Winger', short: 'LW', number: 11, defaultColor: '#f97316', role: 'forward' },
    { id: 'RW', name: 'Right Winger', short: 'RW', number: 7, defaultColor: '#f97316', role: 'forward' },
    { id: 'SS', name: 'Second Striker', short: 'SS', number: 10, defaultColor: '#fb923c', role: 'forward' }
  ],

  // Default formation (4-3-3)
  // Coordinates: 0-100 range, scaled by PlaybookStudio to viewBox (154.4 height)
  // GK near own goal (~y=3), defenders (~y=15-20), midfield (~y=35-45), forwards (~y=55-65)
  // This shows ONE team on their half (y=0 to ~y=50 is own half, y=50+ is opponent half)
  // For full-pitch view, spread evenly: GK at y=3, ST at y=95
  defaultFormation: {
    name: '4-3-3',
    positions: {
      GK: { x: 50, y: 3 },
      LB: { x: 12, y: 20 },
      CB1: { x: 35, y: 15 },
      CB2: { x: 65, y: 15 },
      RB: { x: 88, y: 20 },
      CDM: { x: 50, y: 35 },
      LM: { x: 25, y: 45 },
      RM: { x: 75, y: 45 },
      LW: { x: 15, y: 65 },
      ST: { x: 50, y: 72 },
      RW: { x: 85, y: 65 }
    }
  },

  formations: [
    { id: '4-4-2', name: '4-4-2', type: 'balanced',
      positions: { GK: {x:50,y:3}, LB: {x:12,y:20}, CB1: {x:35,y:15}, CB2: {x:65,y:15}, RB: {x:88,y:20}, LM: {x:12,y:45}, CM1: {x:38,y:40}, CM2: {x:62,y:40}, RM: {x:88,y:45}, ST1: {x:38,y:68}, ST2: {x:62,y:68} }},
    { id: '4-3-3', name: '4-3-3', type: 'attacking',
      positions: { GK: {x:50,y:3}, LB: {x:12,y:20}, CB1: {x:35,y:15}, CB2: {x:65,y:15}, RB: {x:88,y:20}, CDM: {x:50,y:35}, CM1: {x:28,y:45}, CM2: {x:72,y:45}, LW: {x:15,y:65}, ST: {x:50,y:72}, RW: {x:85,y:65} }},
    { id: '4-2-3-1', name: '4-2-3-1', type: 'balanced',
      positions: { GK: {x:50,y:3}, LB: {x:12,y:20}, CB1: {x:35,y:15}, CB2: {x:65,y:15}, RB: {x:88,y:20}, CDM1: {x:38,y:33}, CDM2: {x:62,y:33}, LM: {x:18,y:52}, CAM: {x:50,y:55}, RM: {x:82,y:52}, ST: {x:50,y:72} }},
    { id: '3-5-2', name: '3-5-2', type: 'balanced',
      positions: { GK: {x:50,y:3}, CB1: {x:25,y:15}, CB2: {x:50,y:12}, CB3: {x:75,y:15}, LWB: {x:8,y:40}, CDM: {x:50,y:35}, CM1: {x:35,y:45}, CM2: {x:65,y:45}, RWB: {x:92,y:40}, ST1: {x:38,y:68}, ST2: {x:62,y:68} }},
    { id: '4-1-4-1', name: '4-1-4-1', type: 'defensive',
      positions: { GK: {x:50,y:3}, LB: {x:12,y:20}, CB1: {x:35,y:15}, CB2: {x:65,y:15}, RB: {x:88,y:20}, CDM: {x:50,y:32}, LM: {x:12,y:48}, CM1: {x:38,y:48}, CM2: {x:62,y:48}, RM: {x:88,y:48}, ST: {x:50,y:70} }},
    { id: '5-3-2', name: '5-3-2', type: 'defensive',
      positions: { GK: {x:50,y:3}, LWB: {x:8,y:25}, CB1: {x:28,y:14}, CB2: {x:50,y:11}, CB3: {x:72,y:14}, RWB: {x:92,y:25}, CM1: {x:30,y:40}, CM2: {x:50,y:38}, CM3: {x:70,y:40}, ST1: {x:38,y:65}, ST2: {x:62,y:65} }},
    { id: '4-5-1', name: '4-5-1', type: 'defensive',
      positions: { GK: {x:50,y:3}, LB: {x:12,y:20}, CB1: {x:35,y:15}, CB2: {x:65,y:15}, RB: {x:88,y:20}, LM: {x:12,y:42}, CM1: {x:35,y:40}, CDM: {x:50,y:35}, CM2: {x:65,y:40}, RM: {x:88,y:42}, ST: {x:50,y:70} }},
    { id: '3-4-3', name: '3-4-3', type: 'attacking',
      positions: { GK: {x:50,y:3}, CB1: {x:25,y:15}, CB2: {x:50,y:12}, CB3: {x:75,y:15}, LM: {x:12,y:42}, CM1: {x:38,y:38}, CM2: {x:62,y:38}, RM: {x:88,y:42}, LW: {x:15,y:65}, ST: {x:50,y:72}, RW: {x:85,y:65} }},
    { id: '4-4-2-diamond', name: '4-4-2 Diamond', type: 'balanced',
      positions: { GK: {x:50,y:3}, LB: {x:12,y:20}, CB1: {x:35,y:15}, CB2: {x:65,y:15}, RB: {x:88,y:20}, CDM: {x:50,y:32}, LM: {x:30,y:45}, RM: {x:70,y:45}, CAM: {x:50,y:55}, ST1: {x:38,y:68}, ST2: {x:62,y:68} }},
    // 9v9 formations (U12) — 9 players
    { id: '9v9-3-2-3', name: '9v9: 3-2-3', type: 'balanced', playerCount: 9,
      positions: { GK: {x:50,y:3}, CB1: {x:25,y:18}, CB2: {x:50,y:15}, CB3: {x:75,y:18}, CM1: {x:35,y:40}, CM2: {x:65,y:40}, LW: {x:15,y:62}, ST: {x:50,y:68}, RW: {x:85,y:62} }},
    { id: '9v9-3-3-2', name: '9v9: 3-3-2', type: 'balanced', playerCount: 9,
      positions: { GK: {x:50,y:3}, CB1: {x:25,y:18}, CB2: {x:50,y:15}, CB3: {x:75,y:18}, LM: {x:18,y:42}, CM: {x:50,y:38}, RM: {x:82,y:42}, ST1: {x:38,y:65}, ST2: {x:62,y:65} }},
    { id: '9v9-2-3-2-1', name: '9v9: 2-3-2-1', type: 'attacking', playerCount: 9,
      positions: { GK: {x:50,y:3}, CB1: {x:35,y:16}, CB2: {x:65,y:16}, LM: {x:18,y:35}, CDM: {x:50,y:32}, RM: {x:82,y:35}, CAM1: {x:35,y:52}, CAM2: {x:65,y:52}, ST: {x:50,y:68} }},
    // 7v7 formations (U10) — 7 players
    { id: '7v7-2-3-1', name: '7v7: 2-3-1', type: 'balanced', playerCount: 7,
      positions: { GK: {x:50,y:3}, CB1: {x:30,y:18}, CB2: {x:70,y:18}, LM: {x:18,y:42}, CM: {x:50,y:38}, RM: {x:82,y:42}, ST: {x:50,y:65} }},
    { id: '7v7-3-2-1', name: '7v7: 3-2-1', type: 'defensive', playerCount: 7,
      positions: { GK: {x:50,y:3}, CB1: {x:25,y:18}, CB2: {x:50,y:15}, CB3: {x:75,y:18}, CM1: {x:35,y:42}, CM2: {x:65,y:42}, ST: {x:50,y:65} }},
    { id: '7v7-2-1-2-1', name: '7v7: 2-1-2-1', type: 'attacking', playerCount: 7,
      positions: { GK: {x:50,y:3}, CB1: {x:32,y:18}, CB2: {x:68,y:18}, CDM: {x:50,y:32}, LM: {x:25,y:50}, RM: {x:75,y:50}, ST: {x:50,y:65} }}
  ],

  actions: {
    offense: [
      { id: 'run', name: 'Run', icon: '🏃', lineStyle: 'dashed', category: 'movement' },
      { id: 'sprint', name: 'Sprint', icon: '💨', lineStyle: 'solid', category: 'movement' },
      { id: 'overlap', name: 'Overlapping Run', icon: '↗️', lineStyle: 'dashed', category: 'movement' },
      { id: 'underlap', name: 'Underlapping Run', icon: '↙️', lineStyle: 'dashed', category: 'movement' },
      { id: 'diagonal-run', name: 'Diagonal Run', icon: '↗️', lineStyle: 'dashed', category: 'movement' },
      { id: 'check-run', name: 'Checking Run', icon: '↩️', lineStyle: 'dashed', category: 'movement' },
      { id: 'blind-side', name: 'Blindside Run', icon: '👁️', lineStyle: 'dashed', category: 'movement' },
      { id: 'dribble', name: 'Dribble', icon: '⚽', lineStyle: 'solid-ball', category: 'ball' },
      { id: 'pass', name: 'Pass', icon: '➡️', lineStyle: 'arrow', category: 'ball' },
      { id: 'through-ball', name: 'Through Ball', icon: '⬆️', lineStyle: 'arrow-through', category: 'ball' },
      { id: 'cross', name: 'Cross', icon: '↗️', lineStyle: 'arrow-lob', category: 'ball' },
      { id: 'lob', name: 'Lob / Chip', icon: '⬆️', lineStyle: 'arrow-lob', category: 'ball' },
      { id: 'back-pass', name: 'Back Pass', icon: '⬇️', lineStyle: 'arrow', category: 'ball' },
      { id: 'switch-play', name: 'Switch Play', icon: '↔️', lineStyle: 'arrow', category: 'ball' },
      { id: 'one-two', name: 'One-Two (Wall Pass)', icon: '🔁', lineStyle: 'arrow', category: 'ball' },
      { id: 'flick', name: 'Flick On', icon: '👆', lineStyle: 'arrow-short', category: 'ball' },
      { id: 'lay-off', name: 'Lay Off', icon: '⬇️', lineStyle: 'arrow-short', category: 'ball' },
      { id: 'shoot', name: 'Shoot', icon: '🎯', lineStyle: 'shot', category: 'scoring' },
      { id: 'header', name: 'Header', icon: '🗣️', lineStyle: 'shot', category: 'scoring' },
      { id: 'volley', name: 'Volley', icon: '💥', lineStyle: 'shot', category: 'scoring' },
      { id: 'free-kick', name: 'Free Kick', icon: '🎯', lineStyle: 'shot', category: 'set-piece' },
      { id: 'corner-kick', name: 'Corner Kick', icon: '🚩', lineStyle: 'arrow-lob', category: 'set-piece' },
      { id: 'throw-in', name: 'Throw In', icon: '🤾', lineStyle: 'arrow', category: 'set-piece' },
      { id: 'goal-kick', name: 'Goal Kick', icon: '🥅', lineStyle: 'arrow-lob', category: 'set-piece' },
      { id: 'penalty', name: 'Penalty Kick', icon: '⚽', lineStyle: 'shot', category: 'set-piece' },
      { id: 'skill-move', name: 'Skill Move', icon: '🌀', lineStyle: 'zigzag', category: 'ball' },
      { id: 'nutmeg', name: 'Nutmeg', icon: '🥜', lineStyle: 'zigzag', category: 'ball' },
      { id: 'step-over', name: 'Step Over', icon: '🔄', lineStyle: 'zigzag', category: 'ball' },
      { id: 'hold-up', name: 'Hold Up Play', icon: '✋', lineStyle: 'none', category: 'ball' },
      { id: 'drop-deep', name: 'Drop Deep', icon: '⬇️', lineStyle: 'dashed', category: 'movement' },
      { id: 'press-trigger', name: 'Press Trigger', icon: '🎯', lineStyle: 'solid', category: 'pressing' }
    ],
    defense: [
      { id: 'mark', name: 'Mark Player', icon: '👤', lineStyle: 'solid', category: 'individual' },
      { id: 'track-run', name: 'Track Runner', icon: '👁️', lineStyle: 'dashed', category: 'individual' },
      { id: 'tackle', name: 'Tackle', icon: '🦶', lineStyle: 'none', category: 'individual' },
      { id: 'slide-tackle', name: 'Slide Tackle', icon: '💨', lineStyle: 'none', category: 'individual' },
      { id: 'block-shot', name: 'Block Shot', icon: '🖐️', lineStyle: 'none', category: 'individual' },
      { id: 'intercept', name: 'Intercept', icon: '✋', lineStyle: 'none', category: 'individual' },
      { id: 'press', name: 'Press', icon: '🏃‍♂️', lineStyle: 'solid', category: 'pressing' },
      { id: 'cover', name: 'Cover / Support', icon: '🛡️', lineStyle: 'dashed', category: 'team' },
      { id: 'shift', name: 'Defensive Shift', icon: '➡️', lineStyle: 'dashed', category: 'team' },
      { id: 'drop-back', name: 'Drop Back', icon: '⬇️', lineStyle: 'dashed', category: 'team' },
      { id: 'squeeze', name: 'Squeeze / Compact', icon: '↔️', lineStyle: 'dashed', category: 'team' },
      { id: 'offside-trap', name: 'Offside Trap', icon: '🪤', lineStyle: 'solid', category: 'team' },
      { id: 'clear', name: 'Clear', icon: '🦶', lineStyle: 'arrow', category: 'individual' },
      { id: 'gk-save', name: 'GK Save', icon: '🧤', lineStyle: 'none', category: 'goalkeeper' },
      { id: 'gk-punch', name: 'GK Punch', icon: '👊', lineStyle: 'none', category: 'goalkeeper' },
      { id: 'gk-distribute', name: 'GK Distribution', icon: '📤', lineStyle: 'arrow', category: 'goalkeeper' },
      { id: 'gk-sweep', name: 'GK Sweep', icon: '🏃', lineStyle: 'dashed', category: 'goalkeeper' },
      { id: 'jockey', name: 'Jockey / Delay', icon: '🐎', lineStyle: 'dashed', category: 'individual' },
      { id: 'wall', name: 'Defensive Wall', icon: '🧱', lineStyle: 'none', category: 'set-piece' }
    ],
    transition: [
      { id: 'counter-attack', name: 'Counter Attack', icon: '🚀', lineStyle: 'solid', category: 'transition' },
      { id: 'quick-restart', name: 'Quick Restart', icon: '⚡', lineStyle: 'arrow', category: 'transition' },
      { id: 'recovery-run', name: 'Recovery Run', icon: '🏃‍♂️⬅️', lineStyle: 'dashed', category: 'transition' },
      { id: 'build-up', name: 'Build Up Play', icon: '🔼', lineStyle: 'dashed', category: 'transition' },
      { id: 'possession', name: 'Keep Possession', icon: '🔄', lineStyle: 'dashed', category: 'transition' }
    ]
  },

  playTypes: {
    offense: [
      { id: 'build-up', label: 'Build Up Play' },
      { id: 'wing-play', label: 'Wing Play' },
      { id: 'through-middle', label: 'Through the Middle' },
      { id: 'counter', label: 'Counter Attack' },
      { id: 'possession', label: 'Possession' },
      { id: 'direct', label: 'Direct Play' },
      { id: 'overload', label: 'Overload' },
      { id: 'combination', label: 'Combination Play' }
    ],
    defense: [
      { id: 'high-press', label: 'High Press' },
      { id: 'mid-block', label: 'Mid Block' },
      { id: 'low-block', label: 'Low Block' },
      { id: 'man-marking', label: 'Man Marking' },
      { id: 'zonal', label: 'Zonal Marking' },
      { id: 'gegenpressing', label: 'Gegenpressing' }
    ],
    setPiece: [
      { id: 'corner', label: 'Corner Kick' },
      { id: 'free-kick-direct', label: 'Direct Free Kick' },
      { id: 'free-kick-indirect', label: 'Indirect Free Kick' },
      { id: 'throw-in', label: 'Throw In' },
      { id: 'goal-kick', label: 'Goal Kick' },
      { id: 'penalty', label: 'Penalty Kick' },
      { id: 'kickoff', label: 'Kickoff' }
    ]
  },

  statCategories: [
    { id: 'goals', name: 'Goals', short: 'G', type: 'counting' },
    { id: 'assists', name: 'Assists', short: 'A', type: 'counting' },
    { id: 'shots', name: 'Shots', short: 'SH', type: 'counting' },
    { id: 'sot', name: 'Shots on Target', short: 'SOT', type: 'counting' },
    { id: 'passes', name: 'Passes', short: 'PAS', type: 'counting' },
    { id: 'passAcc', name: 'Pass Accuracy %', short: 'PA%', type: 'percentage' },
    { id: 'crosses', name: 'Crosses', short: 'CRS', type: 'counting' },
    { id: 'tackles', name: 'Tackles', short: 'TCK', type: 'counting' },
    { id: 'interceptions', name: 'Interceptions', short: 'INT', type: 'counting' },
    { id: 'clearances', name: 'Clearances', short: 'CLR', type: 'counting' },
    { id: 'fouls', name: 'Fouls Committed', short: 'FC', type: 'counting' },
    { id: 'fouled', name: 'Fouls Won', short: 'FW', type: 'counting' },
    { id: 'yellowCards', name: 'Yellow Cards', short: 'YC', type: 'counting' },
    { id: 'redCards', name: 'Red Cards', short: 'RC', type: 'counting' },
    { id: 'saves', name: 'Saves (GK)', short: 'SAV', type: 'counting' },
    { id: 'minutes', name: 'Minutes', short: 'MIN', type: 'time' },
    { id: 'dribbles', name: 'Successful Dribbles', short: 'DRB', type: 'counting' },
    { id: 'aerials', name: 'Aerial Duels Won', short: 'AER', type: 'counting' },
    { id: 'offsides', name: 'Offsides', short: 'OFF', type: 'counting' }
  ],

  scoutingSkills: [
    { id: 'pace', name: 'Pace', icon: '🏃' },
    { id: 'shooting', name: 'Shooting', icon: '🎯' },
    { id: 'passing', name: 'Passing', icon: '➡️' },
    { id: 'dribbling', name: 'Dribbling', icon: '⚽' },
    { id: 'defending', name: 'Defending', icon: '🛡️' },
    { id: 'physical', name: 'Physical', icon: '💪' },
    { id: 'vision', name: 'Vision', icon: '👁️' },
    { id: 'positioning', name: 'Positioning', icon: '📍' }
  ],

  court: {
    width: 75,   // yards
    length: 115, // yards
    penaltyAreaWidth: 44,
    penaltyAreaLength: 18,
    goalAreaWidth: 20,
    goalAreaLength: 6,
    centerCircleRadius: 10,
    penaltySpotDist: 12,
    goalWidth: 8,
    cornerArcRadius: 1,
    markings: [
      'center-circle', 'center-line', 'penalty-area', 'goal-area',
      'penalty-spot', 'corner-arc', 'goal', 'touchline', 'goal-line',
      'center-spot', 'penalty-arc'
    ]
  }
};

export default soccer;
